import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/config";
import { MapsPlace, parseGoogleMapsUrl } from "./googleMapsUrl";

export type FilledPlace = MapsPlace & { phone: string };

type PlaceHit = {
  name: string;
  address: string;
  phone: string;
};

function keysFrom(data: Record<string, unknown> | undefined) {
  const names = ["mapsBrowserKey", "placesApiKey", "mapsApiKey", "geocodingApiKey"];
  return names
    .map((name) => String(data?.[name] || "").trim())
    .filter((value, index, list) => value && list.indexOf(value) === index);
}

async function placesSearch(apiKey: string, place: MapsPlace): Promise<PlaceHit | null> {
  const textQuery = place.name || place.address;
  if (!textQuery) return null;
  const body: Record<string, unknown> = {
    textQuery,
    languageCode: "fr",
    regionCode: "ca",
  };
  const latitude = Number(place.latitude);
  const longitude = Number(place.longitude);
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    body.locationBias = {
      circle: {
        center: { latitude, longitude },
        radius: 500,
      },
    };
  }
  const response = await fetch("https://places.googleapis.com/v1/places:searchText", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": apiKey,
      "X-Goog-FieldMask": "places.displayName,places.formattedAddress,places.nationalPhoneNumber,places.internationalPhoneNumber",
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) return null;
  const json = await response.json();
  const hit = Array.isArray(json?.places) ? json.places[0] : null;
  if (!hit) return null;
  return {
    name: String(hit.displayName?.text || "").trim(),
    address: String(hit.formattedAddress || "").trim(),
    phone: String(hit.nationalPhoneNumber || hit.internationalPhoneNumber || "").trim(),
  };
}

async function addressFromGeocode(place: MapsPlace): Promise<string> {
  const query = place.name || place.address;
  if (!query) return "";
  const call = httpsCallable(functions, "geocodeAddress");
  const latitude = Number(place.latitude);
  const longitude = Number(place.longitude);
  const result = await call({
    query,
    regionCode: "CA",
    radiusMeters: 2000,
    storeLatLng: Number.isFinite(latitude) && Number.isFinite(longitude) ? { lat: latitude, lng: longitude } : null,
  });
  const data = result.data as { results?: Array<{ formattedAddress?: string }> };
  return String(data?.results?.[0]?.formattedAddress || "").trim();
}

export async function lookupGooglePlace(raw: string): Promise<FilledPlace> {
  const place = parseGoogleMapsUrl(raw);
  const filled: FilledPlace = { ...place, phone: "" };
  let hit: PlaceHit | null = null;
  try {
    const snap = await getDoc(doc(db, "googleMapsConfig", "global"));
    const keys = keysFrom(snap.data() as Record<string, unknown> | undefined);
    for (const key of keys) {
      hit = await placesSearch(key, place);
      if (hit?.address || hit?.phone) break;
    }
  } catch {
    hit = null;
  }
  if (hit?.name && !filled.name) filled.name = hit.name;
  if (hit?.address) filled.address = hit.address;
  if (hit?.phone) filled.phone = hit.phone;
  if (!filled.address) {
    try {
      filled.address = await addressFromGeocode(place);
    } catch {
      filled.address = place.address;
    }
  }
  if (!filled.address && place.latitude && place.longitude) {
    try {
      filled.address = await addressFromGeocode({ ...place, name: "", address: `${place.latitude},${place.longitude}` });
    } catch {
      filled.address = "";
    }
  }
  return filled;
}
